export type ModelId = 'gpt-6.1-sol' | 'gpt-6-luna' | 'gemini-2.5-flash-lite';
export type ReasoningEffort = 'none' | 'low' | 'medium' | 'high';
export type ModelProvider = 'openai' | 'google';

export interface ModelConfig {
  id: ModelId;
  displayName: string;
  description: string;
  provider: ModelProvider;
  reasoningEfforts: ReasoningEffort[];
  defaultReasoning: ReasoningEffort | null;
  chatbot: boolean;
}

export const MODELS: ModelConfig[] = [
  {
    id: 'gpt-6.1-sol',
    displayName: 'Sol',
    description:
      'Most capable. Best for complex reasoning, detailed writing, and following nuanced instructions. Recommended default.',
    provider: 'openai',
    reasoningEfforts: ['low', 'medium', 'high'],
    defaultReasoning: 'low',
    chatbot: true,
  },
  {
    id: 'gpt-6-luna',
    displayName: 'Luna',
    description:
      'Fast and efficient. Turn reasoning off for quick drafts, or raise it for higher-quality output. Great balance of speed and quality.',
    provider: 'openai',
    reasoningEfforts: ['none', 'low', 'medium', 'high'],
    defaultReasoning: 'low',
    chatbot: true,
  },
  {
    id: 'gemini-2.5-flash-lite',
    displayName: 'Gemini 2.5 Flash Lite',
    description:
      'Fast and lightweight. Best for quick responses, simple tasks, and high-volume use. Lower cost, with decent quality.',
    provider: 'google',
    reasoningEfforts: [],
    defaultReasoning: null,
    chatbot: false,
  },
];

export function isModelId(value: unknown): value is ModelId {
  return MODELS.some((m) => m.id === value);
}

export function getModel(id: ModelId): ModelConfig {
  const model = MODELS.find((m) => m.id === id);
  if (!model) throw new Error(`Unknown model: ${id}`);
  return model;
}

/** The effort to send for `modelId`, or `null` when the model doesn't accept it. */
export function effortForRequest(
  modelId: ModelId,
  effort: ReasoningEffort | null | undefined,
): ReasoningEffort | null {
  return effort != null && getModel(modelId).reasoningEfforts.includes(effort) ? effort : null;
}
